import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          email: string
          name: string | null
          role: 'tutor' | 'intake' | 'admin' | 'super_admin'
          avatar_url: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          email: string
          name?: string | null
          role: 'tutor' | 'intake' | 'admin' | 'super_admin'
          avatar_url?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          name?: string | null
          role?: 'tutor' | 'intake' | 'admin' | 'super_admin'
          avatar_url?: string | null
          updated_at?: string
        }
      }
      tutors: {
        Row: {
          id: string
          name: string
          email: string
          phone: string | null
          address: string | null
          level: 'Regular' | 'Premium' | 'Expert'
          qualifications: string[]
          active: boolean
          active_since: string | null
          subjects: string[]
          total_minutes_available: number
          total_minutes_used: number
          avatar_url: string | null
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          email: string
          phone?: string | null
          address?: string | null
          level?: 'Regular' | 'Premium' | 'Expert'
          qualifications?: string[]
          active?: boolean
          active_since?: string | null
          subjects: string[]
          total_minutes_available?: number
          total_minutes_used?: number
          avatar_url?: string | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          name?: string
          email?: string
          phone?: string | null
          address?: string | null
          level?: 'Regular' | 'Premium' | 'Expert'
          qualifications?: string[]
          active?: boolean
          active_since?: string | null
          subjects?: string[]
          total_minutes_available?: number
          total_minutes_used?: number
          avatar_url?: string | null
          notes?: string | null
          updated_at?: string
        }
      }
      tutor_billing: {
        Row: { tutor_id: string; hourly_rate: number | null; updated_at: string }
        Insert: { tutor_id: string; hourly_rate?: number | null; updated_at?: string }
        Update: { tutor_id?: string; hourly_rate?: number | null; updated_at?: string }
      }
      students: {
        Row: {
          id: string
          name: string
          phone: string | null
          address: string | null
          grade: string | null
          tutor_id: string
          guardian_name: string | null
          guardian_email: string | null
          guardian_phone: string | null
          guardian_address: string | null
          balance_minutes: number
          bill_later: boolean
          post_balance_minutes: number
          session_length_minutes: number | null
          status: 'active' | 'inactive'
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          phone?: string | null
          address?: string | null
          grade?: string | null
          tutor_id: string
          guardian_name?: string | null
          guardian_email?: string | null
          guardian_phone?: string | null
          guardian_address?: string | null
          balance_minutes?: number
          bill_later?: boolean
          post_balance_minutes?: number
          session_length_minutes?: number | null
          status?: 'active' | 'inactive'
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          name?: string
          phone?: string | null
          address?: string | null
          grade?: string | null
          tutor_id?: string
          guardian_name?: string | null
          guardian_email?: string | null
          guardian_phone?: string | null
          guardian_address?: string | null
          balance_minutes?: number
          bill_later?: boolean
          post_balance_minutes?: number
          session_length_minutes?: number | null
          status?: 'active' | 'inactive'
          notes?: string | null
          updated_at?: string
        }
      }
      student_billing: {
        Row: { student_id: string; price_per_session: number | null; insurance_coverage_percent: number | null; hourly_rate_override: number | null; updated_at: string }
        Insert: { student_id: string; price_per_session?: number | null; insurance_coverage_percent?: number | null; hourly_rate_override?: number | null; updated_at?: string }
        Update: { student_id?: string; price_per_session?: number | null; insurance_coverage_percent?: number | null; hourly_rate_override?: number | null; updated_at?: string }
      }
      sessions: {
        Row: {
          id: string
          student_id: string
          tutor_id: string
          started_at: string
          ended_at: string
          minutes: number
          notes: string | null
          attachment_filename: string | null
          approval_status: 'pending' | 'approved' | 'rejected'
          approved_by: string | null
          approved_at: string | null
          session_type: 'tutoring' | 'consulting'
          lifecycle_status: 'completed' | 'cancelled'
          is_free: boolean
          cancelled_at: string | null
          cancel_reason: string | null
          consulting_credit_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          student_id: string
          tutor_id: string
          started_at: string
          ended_at: string
          minutes: number
          notes?: string | null
          attachment_filename?: string | null
          approval_status?: 'pending' | 'approved' | 'rejected'
          approved_by?: string | null
          approved_at?: string | null
          session_type?: 'tutoring' | 'consulting'
          lifecycle_status?: 'completed' | 'cancelled'
          is_free?: boolean
          cancelled_at?: string | null
          cancel_reason?: string | null
          consulting_credit_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          student_id?: string
          tutor_id?: string
          started_at?: string
          ended_at?: string
          minutes?: number
          notes?: string | null
          attachment_filename?: string | null
          approval_status?: 'pending' | 'approved' | 'rejected'
          approved_by?: string | null
          approved_at?: string | null
          session_type?: 'tutoring' | 'consulting'
          lifecycle_status?: 'completed' | 'cancelled'
          is_free?: boolean
          cancelled_at?: string | null
          cancel_reason?: string | null
          consulting_credit_id?: string | null
          updated_at?: string
        }
      }
      topups: {
        Row: {
          id: string
          student_id: string
          minutes_added: number
          amount_usd: number
          created_at: string
          method: 'card' | 'bank' | 'cash' | 'pact' | 'clinic'
          entered_by: string | null
          note: string | null
        }
        Insert: {
          id?: string
          student_id: string
          minutes_added: number
          amount_usd: number
          created_at?: string
          method: 'card' | 'bank' | 'cash' | 'pact' | 'clinic'
          entered_by?: string | null
          note?: string | null
        }
        Update: {
          student_id?: string
          minutes_added?: number
          amount_usd?: number
          method?: 'card' | 'bank' | 'cash' | 'pact' | 'clinic'
          entered_by?: string | null
          note?: string | null
        }
      }
      minutes_ledger: {
        Row: {
          id: string
          student_id: string
          delta_minutes: number
          reason: 'session' | 'topup'
          related_id: string
          created_at: string
          running_balance: number
          entered_by: string
          entered_by_role: 'admin' | 'tutor'
          reference: string | null
        }
        Insert: {
          id?: string
          student_id: string
          delta_minutes: number
          reason: 'session' | 'topup'
          related_id: string
          created_at?: string
          running_balance: number
          entered_by: string
          entered_by_role: 'admin' | 'tutor'
          reference?: string | null
        }
        Update: {
          student_id?: string
          delta_minutes?: number
          reason?: 'session' | 'topup'
          related_id?: string
          running_balance?: number
          entered_by?: string
          entered_by_role?: 'admin' | 'tutor'
          reference?: string | null
        }
      }
      consulting_credits: {
        Row: {
          id: string
          student_id: string
          earned_at: string
          earned_at_session_count: number | null
          status: 'available' | 'claimed' | 'revoked'
          followup_status: 'accepted' | 'declined' | null
          followup_at: string | null
          followup_by: string | null
          delivered_session_id: string | null
          revoked_at: string | null
          revoked_reason: string | null
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          student_id: string
          earned_at?: string
          earned_at_session_count?: number | null
          status?: 'available' | 'claimed' | 'revoked'
          followup_status?: 'accepted' | 'declined' | null
          followup_at?: string | null
          followup_by?: string | null
          delivered_session_id?: string | null
          revoked_at?: string | null
          revoked_reason?: string | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          status?: 'available' | 'claimed' | 'revoked'
          followup_status?: 'accepted' | 'declined' | null
          followup_at?: string | null
          followup_by?: string | null
          delivered_session_id?: string | null
          revoked_at?: string | null
          revoked_reason?: string | null
          notes?: string | null
          updated_at?: string
        }
      }
      rate_change_requests: {
        Row: {
          id: string
          student_id: string
          tutor_id: string
          current_rate: number | null
          requested_rate: number
          status: 'pending' | 'approved' | 'rejected'
          requested_by: string | null
          decided_by: string | null
          note: string | null
          created_at: string
          decided_at: string | null
        }
        Insert: {
          id?: string
          student_id: string
          tutor_id: string
          current_rate?: number | null
          requested_rate: number
          status?: 'pending' | 'approved' | 'rejected'
          requested_by?: string | null
          decided_by?: string | null
          note?: string | null
          created_at?: string
          decided_at?: string | null
        }
        Update: {
          current_rate?: number | null
          requested_rate?: number
          status?: 'pending' | 'approved' | 'rejected'
          requested_by?: string | null
          decided_by?: string | null
          note?: string | null
          decided_at?: string | null
        }
      }
      notifications: {
        Row: {
          id: string
          type: 'session_approval' | 'rate_request' | 'consulting_followup' | 'topup' | 'low_balance'
          title: string
          message: string | null
          related_table: string | null
          related_id: string | null
          route: string | null
          severity: 'info' | 'warning' | 'alert'
          target_role: 'admin' | 'tutor'
          target_user_id: string | null
          is_resolved: boolean
          resolved_by: string | null
          resolved_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          type: 'session_approval' | 'rate_request' | 'consulting_followup' | 'topup' | 'low_balance'
          title: string
          message?: string | null
          related_table?: string | null
          related_id?: string | null
          route?: string | null
          severity?: 'info' | 'warning' | 'alert'
          target_role?: 'admin' | 'tutor'
          target_user_id?: string | null
          is_resolved?: boolean
          resolved_by?: string | null
          resolved_at?: string | null
          created_at?: string
        }
        Update: {
          title?: string
          message?: string | null
          route?: string | null
          severity?: 'info' | 'warning' | 'alert'
          is_resolved?: boolean
          resolved_by?: string | null
          resolved_at?: string | null
        }
      }
      activity_log: {
        Row: {
          id: string
          scope: 'sessions' | 'students' | 'tutors' | 'topups' | 'global'
          actor_id: string | null
          actor_email: string | null
          actor_role: string | null
          action: string
          entity_table: string | null
          entity_id: string | null
          summary: string
          metadata: Record<string, any> | null
          created_at: string
        }
        Insert: {
          id?: string
          scope: 'sessions' | 'students' | 'tutors' | 'topups' | 'global'
          actor_id?: string | null
          actor_email?: string | null
          actor_role?: string | null
          action: string
          entity_table?: string | null
          entity_id?: string | null
          summary: string
          metadata?: Record<string, any> | null
          created_at?: string
        }
        Update: {
          summary?: string
          metadata?: Record<string, any> | null
        }
      }
      // supabase/migrations/20261030150000_card_comments.sql. The database fills in the author;
      // nobody updates a comment.
      card_comments: {
        Row: {
          id: string
          card_id: string
          body: string
          created_by: string
          author_email: string | null
          created_at: string
        }
        Insert: {
          card_id: string
          body: string
        }
        Update: Record<string, never>
      }
    }
    Functions: {
      // supabase/migrations/20261030170000_delete_board_column.sql. Returns how many cards it deleted.
      delete_board_column: {
        Args: { p_column_id: string; p_expected_cards?: number | null }
        Returns: number
      }
    }
  }
}